import { Warehouse } from '@inventory/core';
import type { IWarehouseRepository, WarehouseStatusEnum } from '@inventory/core';
import { BasePouchDBAdapter } from './BasePouchDBAdapter';
import type { BasePouchDocument } from '../types';

interface WarehouseDocument extends BasePouchDocument {
  name: string;
  status: WarehouseStatusEnum;
}

export class WarehousePouchDBAdapter extends BasePouchDBAdapter<Warehouse> implements IWarehouseRepository {
  private indexesInitialized: boolean = false;

  constructor(dbName: string) {
    super(dbName, 'warehouse');
    this.initializeIndexes().catch(error => {
      console.error('Error initializing indices:', error);
    });
  }

  private async initializeIndexes(): Promise<void> {
    if (this.indexesInitialized) return;

    try {
      await this.createIndexes();
      this.indexesInitialized = true;
      console.debug('Warehouse indexes initialized successfully');
    } catch (error) {
      console.error('Failed to initialize indexes:', error);
      throw error;
    }
  }

  async save(warehouse: Warehouse): Promise<void> {
    const doc = this.toDocument(warehouse);
    try {
      const existing = await this.db.get(doc._id);
      await this.db.put({ ...doc, _rev: existing._rev ?? '' });
    } catch (error) {
      if ((error as any).status === 404) {
        await this.db.put(doc);
      } else {
        console.error('Error saving warehouse:', error);
        throw error;
      }
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const doc = await this.db.get(id);
      await this.db.remove(doc);
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      throw error;
    }
  }

  private async createIndexes(): Promise<void> {
    try {
      await Promise.all([
        this.createIndex([]),
        this.createIndex(['code']),
        this.createIndex(['status'])
      ]);
    } catch (error) {
      console.error('Error creating warehouse indices:', error);
      throw error;
    }
  }

  protected toDocument(warehouse: Warehouse): WarehouseDocument {
    return {
      _id: warehouse._id,
      _rev: warehouse._rev,
      type: warehouse.type,
      version: warehouse.version,
      createdAt: warehouse.createdAt.toISOString(),
      updatedAt: warehouse.updatedAt.toISOString(),
      name: warehouse.name,
      status: warehouse.status
    };
  }

  protected fromDocument(doc: WarehouseDocument): Warehouse {
    return Warehouse.restore({
      _id: doc._id,
      _rev: doc._rev,
      type: doc.type,
      version: doc.version ?? 1,
      createdAt: new Date(doc.createdAt),
      updatedAt: new Date(doc.updatedAt),
      name: doc.name,
      status: doc.status
    });
  }

  async findByCode(code: string): Promise<Warehouse | null> {
    const results = await this.query({ code });
    return results[0] ?? null;
  }

  async findActive(): Promise<Warehouse[]> {
    return this.query({ status: 'ACTIVE' });
  }
}